import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  adminSearchCoupons,
  adminListSponsored,
  adminSponsor,
  adminUnsponsor,
  adminStats,
  adminClickStats,
  adminRevenue,
  api,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Star, X, DollarSign, Calendar, ShieldCheck, ExternalLink, RefreshCw } from "lucide-react";
import StoreLogo from "@/components/StoreLogo";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function Admin() {
  const { user, loading, openLogin } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sponsored, setSponsored] = useState([]);
  const [stats, setStats] = useState(null);
  const [clickStats, setClickStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [refreshingMoney, setRefreshingMoney] = useState(false);
  const [form, setForm] = useState({}); // { [coupon_id]: { start, end, price } }
  const [searching, setSearching] = useState(false);

  const refresh = async () => {
    try {
      const [sp, st, cs, rv] = await Promise.all([
        adminListSponsored(),
        adminStats(),
        adminClickStats(30),
        adminRevenue(),
      ]);
      setSponsored(sp);
      setStats(st);
      setClickStats(cs);
      setRevenue(rv);
    } catch (e) {
      // 403 handled via UI gate
    }
  };

  const refreshMoney = async () => {
    setRefreshingMoney(true);
    try {
      const rv = await adminRevenue();
      setRevenue(rv);
    } finally {
      setRefreshingMoney(false);
    }
  };

  // Auto-refresh revenue every 30s so the Money Desk stays live
  useEffect(() => {
    if (!user?.is_admin) return;
    const iv = setInterval(() => {
      adminRevenue().then(setRevenue).catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, [user]);

  useEffect(() => {
    if (user?.is_admin) refresh();
  }, [user]);

  useEffect(() => {
    if (!user?.is_admin) return;
    if (!q) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await adminSearchCoupons({ q, limit: 30 });
        setResults(data);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, user]);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-16 text-center text-sm tracking-widest">
        LOADING...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-24 text-center" data-testid="admin-signin-gate">
        <ShieldCheck className="w-10 h-10 mx-auto mb-4" />
        <h1 className="serif text-4xl mb-3">Admin only</h1>
        <p className="text-sm text-[#5c5c5c] mb-6">Sign in with the approved Google account.</p>
        <button
          onClick={() => openLogin("signin")}
          data-testid="admin-signin-btn"
          className="px-6 py-3 border-2 border-[#2d2a32] rounded-full bg-[#2d2a32] text-white font-bold tracking-[0.2em] hard-shadow-sm"
        >
          SIGN IN
        </button>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-24 text-center" data-testid="admin-forbidden">
        <h1 className="serif text-4xl mb-3">Not authorised</h1>
        <p className="text-sm text-[#5c5c5c]">
          This account ({user.email}) isn't on the admin allowlist.
        </p>
      </div>
    );
  }

  const setField = (id, key, val) => setForm((f) => ({ ...f, [id]: { ...(f[id] || {}), [key]: val } }));

  const handleSponsor = async (coupon) => {
    setBusyId(coupon.id);
    const f = form[coupon.id] || {};
    const payload = {
      start: f.start || todayISO(),
      end: f.end || addDaysISO(30),
      price: f.price ? parseFloat(f.price) : null,
    };
    try {
      await adminSponsor(coupon.id, payload);
      toast.success(`${coupon.store} is now SPONSORED`);
      await refresh();
    } catch (e) {
      toast.error("Failed to sponsor deal");
    } finally {
      setBusyId(null);
    }
  };

  const handleUnsponsor = async (coupon) => {
    setBusyId(coupon.id);
    try {
      await adminUnsponsor(coupon.id);
      toast.success(`Unpinned ${coupon.store}`);
      await refresh();
    } catch (e) {
      toast.error("Failed to unsponsor");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8" data-testid="admin-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.35em] text-[#5c5c5c] mb-2">
            SPONSORED SLOTS · ADMIN
          </div>
          <h1 className="serif text-5xl md:text-6xl leading-none tracking-tight">
            The Money <span className="italic text-[#f2a93b]">Desk.</span>
          </h1>
          <p className="text-sm text-[#5c5c5c] mt-2">
            Pin any deal to the top of the feed. Set your window. Log what the retailer paid.
          </p>
        </div>
        {stats && (
          <div className="grid grid-cols-3 gap-3" data-testid="admin-stats">
            <StatCard label="ACTIVE" value={stats.active_sponsored} tone="shadow-teal" />
            <StatCard label="TOTAL" value={stats.total_sponsored} tone="shadow-mustard" />
            <StatCard
              label="REVENUE"
              value={`$${(stats.total_revenue_usd || 0).toLocaleString()}`}
              tone="shadow-coral"
            />
          </div>
        )}
      </div>

      {/* Money Desk — live revenue */}
      {revenue && (
        <section className="mb-10" data-testid="money-desk">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <h2 className="serif text-3xl">The Money Desk · live</h2>
            <div className="flex items-center gap-3">
              <span className="text-[10px] tracking-[0.25em] text-[#5c5c5c]">
                AS OF {new Date(revenue.as_of).toLocaleTimeString()}
              </span>
              <button
                onClick={refreshMoney}
                data-testid="money-refresh-btn"
                disabled={refreshingMoney}
                className="h-8 px-3 border-2 border-[#2d2a32] rounded-full bg-white hover:bg-[#4ecdc4] text-[10px] tracking-[0.2em] font-bold inline-flex items-center gap-1 disabled:opacity-50"
                aria-label="Refresh revenue"
              >
                <RefreshCw className={`w-3 h-3 ${refreshingMoney ? "animate-spin" : ""}`} />
                REFRESH
              </button>
            </div>
          </div>

          {/* Four windows */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {[
              { key: "today", label: "TODAY", tint: "#7dbb6e" },
              { key: "week", label: "7 DAYS", tint: "#4ecdc4" },
              { key: "month", label: "30 DAYS", tint: "#f7c873" },
              { key: "all_time", label: "ALL TIME", tint: "#ff6b6b" },
            ].map((w) => {
              const b = revenue.breakdown[w.key] || {};
              return (
                <div
                  key={w.key}
                  data-testid={`money-window-${w.key}`}
                  className="border-2 border-[#2d2a32] rounded-2xl p-4 bg-white hard-shadow-sm"
                  style={{ background: `color-mix(in oklab, ${w.tint} 25%, #ffffff)` }}
                >
                  <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c] font-bold mb-1">
                    {w.label}
                  </div>
                  <div className="serif text-4xl leading-none">
                    ${(b.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                  <div className="mt-3 space-y-0.5 text-[11px] text-[#3a3a38] font-medium">
                    <div className="flex justify-between"><span>Sponsors</span><span className="mono">${(b.sponsored_stripe || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>Pro subs</span><span className="mono">${(b.pro_subscriptions || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>Manual</span><span className="mono">${(b.manual_sponsorships || 0).toFixed(2)}</span></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recent transactions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 border-2 border-[#2d2a32] rounded-xl bg-white p-4" data-testid="recent-txns">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c] font-bold">RECENT PAYMENTS</div>
                <div className="text-[10px] tracking-[0.2em] text-[#5c5c5c]">{revenue.recent_transactions.length} in last 20</div>
              </div>
              {revenue.recent_transactions.length === 0 ? (
                <div className="py-6 text-center">
                  <p className="text-sm text-[#5c5c5c] italic">No completed payments yet.</p>
                  <p className="text-[11px] text-[#5c5c5c] mt-1">When retailers pay for a sponsor slot or users go Pro, they show up here in real time.</p>
                </div>
              ) : (
                <ul className="divide-y divide-dashed divide-[#2d2a32]/30">
                  {revenue.recent_transactions.map((t) => (
                    <li key={t.session_id} className="py-2 flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <div className="font-bold">
                          {t.type === "sponsor" ? `Sponsor · ${t.tier || "?"} · ${t.days || "?"}d` : `Pro · ${t.lookup_key || "?"}`}
                        </div>
                        <div className="text-[11px] text-[#5c5c5c]">{new Date(t.created_at).toLocaleString()}</div>
                      </div>
                      <div className="mono text-[15px] font-bold text-[#2d2a32]">${t.amount_usd.toFixed(2)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* External sources */}
            <div className="border-2 border-[#2d2a32] rounded-xl bg-white p-4" data-testid="external-sources">
              <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c] font-bold mb-2">EXTERNAL SOURCES</div>
              <p className="text-[11px] text-[#5c5c5c] leading-snug mb-3">
                These earnings are tracked by third parties. Tap through to see live numbers.
              </p>
              <div className="space-y-2">
                {[
                  { key: "skimlinks", label: "Skimlinks", ...revenue.external_sources.skimlinks },
                  { key: "amazon", label: "Amazon Associates", ...revenue.external_sources.amazon },
                  { key: "adsense", label: "Google AdSense", ...revenue.external_sources.adsense },
                ].map((s) => (
                  <a
                    key={s.key}
                    href={s.dashboard_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`ext-${s.key}`}
                    className="flex items-center justify-between gap-2 p-2 border-2 rounded-lg hover:bg-[#fff8ef]"
                    style={{ borderColor: s.enabled ? "#2d2a32" : "#2d2a3244" }}
                  >
                    <div className="min-w-0">
                      <div className="font-bold text-sm">{s.label}</div>
                      <div className="text-[10px] text-[#5c5c5c]">
                        {s.enabled ? "CONNECTED" : "NOT CONFIGURED"}
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 shrink-0 opacity-70" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTR / Click stats */}
      {clickStats && (
        <section className="mb-10" data-testid="ctr-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="serif text-3xl">Click stats · last 30 days</h2>
            <span className="text-[10px] tracking-[0.25em] text-[#5c5c5c]">
              {clickStats.total_clicks} TOTAL · {clickStats.sponsored_clicks} FROM SPONSORED
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border-2 border-[#2d2a32] rounded-xl bg-white p-4">
              <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c] mb-2">TOP DEALS BY CLICKS</div>
              {clickStats.per_coupon.length === 0 ? (
                <p className="text-sm text-[#5c5c5c] italic">No clicks yet.</p>
              ) : (
                <ul className="space-y-1">
                  {clickStats.per_coupon.slice(0, 10).map((r) => (
                    <li key={r.coupon_id} className="flex items-center justify-between text-sm">
                      <span className="truncate">
                        {r.sponsored && <span className="mr-1">★</span>}
                        {r.store}
                      </span>
                      <span className="mono font-bold">{r.clicks}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-2 border-[#2d2a32] rounded-xl bg-white p-4">
              <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c] mb-2">TOP STORES BY CLICKS</div>
              {clickStats.per_store.length === 0 ? (
                <p className="text-sm text-[#5c5c5c] italic">No clicks yet.</p>
              ) : (
                <ul className="space-y-1">
                  {clickStats.per_store.slice(0, 10).map((r) => (
                    <li key={r.store} className="flex items-center justify-between text-sm">
                      <span className="truncate">{r.store}</span>
                      <span className="mono font-bold">{r.clicks}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Active Sponsored */}
      <SundayClipComposer />

      <section className="mb-12" data-testid="active-sponsored-section">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="serif text-3xl">Currently Pinned</h2>
          <span className="text-[10px] tracking-[0.25em] text-[#5c5c5c]">
            {sponsored.length} DEAL{sponsored.length !== 1 ? "S" : ""}
          </span>
        </div>
        {sponsored.length === 0 ? (
          <div className="border-2 border-dashed border-[#2d2a32]/40 p-8 text-center bg-white rounded-xl">
            <p className="text-sm text-[#5c5c5c]">No sponsored deals yet. Pin one below.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sponsored.map((c) => (
              <SponsoredRow
                key={c.id}
                coupon={c}
                onUnsponsor={() => handleUnsponsor(c)}
                busy={busyId === c.id}
              />
            ))}
          </div>
        )}
      </section>

      {/* Search + Pin */}
      <section data-testid="admin-search-section">
        <h2 className="serif text-3xl mb-3">Pin a New Deal</h2>
        <div className="relative max-w-2xl mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c5c5c]" />
          <Input
            data-testid="admin-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by store or title..."
            className="pl-11 h-12 rounded-full border-2 border-[#2d2a32] bg-white shadow-teal"
          />
        </div>
        {searching && <p className="text-xs text-[#5c5c5c] mb-4">Searching...</p>}
        <div className="space-y-3">
          {results.map((c) => {
            const f = form[c.id] || {};
            return (
              <div
                key={c.id}
                data-testid={`admin-result-${c.id}`}
                className="border-2 border-[#2d2a32] rounded-xl bg-white p-4 flex flex-col md:flex-row md:items-center gap-4 hard-shadow-sm"
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <StoreLogo store={c.store} storeUrl={c.store_url} size={44} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] tracking-[0.2em] text-[#5c5c5c]">
                      {c.category?.toUpperCase()} · {c.discount_label}
                      {c.is_sponsored && (
                        <span className="ml-2 px-1.5 py-0.5 bg-[#f7c873] text-[#2d2a32] font-black">
                          ★ SPONSORED
                        </span>
                      )}
                    </div>
                    <div className="serif text-xl truncate">{c.store}</div>
                    <div className="text-xs text-[#3a3a38] truncate">{c.title}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-[10px] tracking-[0.2em] text-[#5c5c5c] flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> START
                  </label>
                  <input
                    type="date"
                    data-testid={`admin-start-${c.id}`}
                    value={f.start || todayISO()}
                    onChange={(e) => setField(c.id, "start", e.target.value)}
                    className="h-9 border-2 border-[#2d2a32] rounded-md px-2 text-xs bg-white"
                  />
                  <label className="text-[10px] tracking-[0.2em] text-[#5c5c5c]">END</label>
                  <input
                    type="date"
                    data-testid={`admin-end-${c.id}`}
                    value={f.end || addDaysISO(30)}
                    onChange={(e) => setField(c.id, "end", e.target.value)}
                    className="h-9 border-2 border-[#2d2a32] rounded-md px-2 text-xs bg-white"
                  />
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3 text-[#5c5c5c]" />
                    <input
                      type="number"
                      data-testid={`admin-price-${c.id}`}
                      value={f.price || ""}
                      onChange={(e) => setField(c.id, "price", e.target.value)}
                      placeholder="0.00"
                      className="w-24 h-9 border-2 border-[#2d2a32] rounded-md px-2 mono text-xs bg-white"
                    />
                  </div>
                  {c.is_sponsored ? (
                    <button
                      onClick={() => handleUnsponsor(c)}
                      disabled={busyId === c.id}
                      data-testid={`admin-unsponsor-${c.id}`}
                      className="h-9 px-4 border-2 border-[#2d2a32] rounded-full bg-white hover:bg-[#2d2a32] hover:text-white text-[11px] tracking-[0.2em] font-bold disabled:opacity-50"
                    >
                      <X className="w-3 h-3 inline mr-1" /> UNPIN
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSponsor(c)}
                      disabled={busyId === c.id}
                      data-testid={`admin-sponsor-${c.id}`}
                      className="h-9 px-4 border-2 border-[#2d2a32] rounded-full bg-[#f7c873] hover:bg-[#f2a93b] text-[#2d2a32] text-[11px] tracking-[0.2em] font-bold disabled:opacity-50"
                    >
                      <Star className="w-3 h-3 inline mr-1" /> PIN AS SPONSORED
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {!q && (
            <p className="text-sm text-[#5c5c5c] italic">Type above to search 14,000+ deals.</p>
          )}
          {q && !searching && results.length === 0 && (
            <p className="text-sm text-[#5c5c5c] italic">No matches for "{q}".</p>
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`border-2 border-[#2d2a32] rounded-xl bg-white px-4 py-3 ${tone}`}>
      <div className="serif text-3xl leading-none">{value}</div>
      <div className="text-[10px] tracking-[0.2em] mt-1">{label}</div>
    </div>
  );
}

function SponsoredRow({ coupon, onUnsponsor, busy }) {
  return (
    <div
      data-testid={`sponsored-row-${coupon.id}`}
      className="border-2 border-[#f2a93b] rounded-xl p-4 bg-gradient-to-br from-[#fffaef] to-white flex items-center gap-3 shadow-mustard"
    >
      <StoreLogo store={coupon.store} storeUrl={coupon.store_url} size={44} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.2em] text-[#5c5c5c]">
          {coupon.discount_label} · {coupon.category?.toUpperCase()}
        </div>
        <div className="serif text-lg truncate">{coupon.store}</div>
        <div className="text-[11px] text-[#5c5c5c]">
          {coupon.sponsored_start || "—"} → {coupon.sponsored_end || "—"}
          {coupon.sponsored_price != null && (
            <span className="ml-2 font-bold text-[#2d2a32]">
              ${Number(coupon.sponsored_price).toFixed(2)}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={onUnsponsor}
        disabled={busy}
        data-testid={`unpin-btn-${coupon.id}`}
        className="h-9 px-3 border-2 border-[#2d2a32] rounded-full bg-white hover:bg-[#ff6b6b] hover:text-white text-[11px] tracking-[0.2em] font-bold disabled:opacity-50"
      >
        UNPIN
      </button>
    </div>
  );
}

// The Sunday Clip · weekly editor's-letter composer.
// Deliberately kept as one compact form — this email is written, not templated.
function SundayClipComposer() {
  const [intro, setIntro] = useState(
    "Hope your weekend's off to a good one. This week's finds are a mix of grocery reliables and one absolute steal that surprised me — let's get into it."
  );
  const [heroA, setHeroA] = useState("");
  const [heroB, setHeroB] = useState("");
  const [heroC, setHeroC] = useState("");
  const [tip, setTip] = useState("");
  const [mystery, setMystery] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const draft = () => ({
    intro: intro.trim(),
    hero_ids: [heroA, heroB, heroC].map((s) => s.trim()).filter(Boolean),
    tip: tip.trim(),
    mystery_teaser: mystery.trim(),
  });

  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sunday-clip/preview", draft());
      setPreview(data);
      toast.success(`Preview ready — ${data.resolved_count} deals resolved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const doAutoDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sunday-clip/auto-draft");
      if (data.intro) setIntro(data.intro);
      if (data.hero_ids?.[0]) setHeroA(data.hero_ids[0]);
      if (data.hero_ids?.[1]) setHeroB(data.hero_ids[1]);
      if (data.hero_ids?.[2]) setHeroC(data.hero_ids[2]);
      if (data.tip) setTip(data.tip);
      if (data.mystery_teaser) setMystery(data.mystery_teaser);
      toast.success("Claude drafted it — edit and preview when ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally {
      setBusy(false);
    }
  };

  const doSendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sunday-clip/send-test", draft());
      toast.success(`Test sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send test failed");
    } finally {
      setBusy(false);
    }
  };

  const doBroadcast = async () => {
    if (!window.confirm("Send The Sunday Clip to ALL opted-in users? This can't be undone.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sunday-clip/send", draft());
      toast.success(`Sent to ${data.sent}/${data.opted_in} subscribers`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="sunday-clip-composer"
      className="mb-12 border-2 border-[#2d2a32] rounded-xl p-5 bg-[#faf5e6]"
    >
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <h2 className="serif text-3xl">The Sunday Clip · weekly editor letter</h2>
        <span className="text-[10px] tracking-[0.25em] text-[#5c5c5c]">
          RETENTION EMAIL · WEEKLY BROADCAST
        </span>
      </div>
      <p className="text-sm text-[#3a3a38] mb-4 max-w-2xl">
        Write a short letter, pick 3 hero coupon IDs, add an insider tip + a mystery deal teaser (reply-to-unlock).
        Preview first, send a test to yourself, then broadcast to everyone opted in.
      </p>

      <div className="grid grid-cols-1 gap-3">
        <div>
          <label className="text-[10px] tracking-[0.2em] text-[#5c5c5c] font-semibold">EDITOR&#39;S INTRO</label>
          <textarea
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            data-testid="sunday-clip-intro"
            rows={3}
            className="w-full border-2 border-[#2d2a32] rounded-md bg-white p-3 text-sm mt-1 focus:outline-none focus:border-[#ff6b6b]"
            placeholder="2-3 sentences with real voice — reads like a friend, not a mailer."
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[["A", heroA, setHeroA], ["B", heroB, setHeroB], ["C", heroC, setHeroC]].map(([lbl, val, set]) => (
            <div key={lbl}>
              <label className="text-[10px] tracking-[0.2em] text-[#5c5c5c] font-semibold">HERO CODE #{lbl} · coupon id</label>
              <Input
                value={val}
                onChange={(e) => set(e.target.value)}
                data-testid={`sunday-clip-hero-${lbl.toLowerCase()}`}
                placeholder="cpn_xxxxxxxx"
                className="mt-1 border-2 border-[#2d2a32] rounded-md bg-white h-10 text-sm"
              />
            </div>
          ))}
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] text-[#5c5c5c] font-semibold">INSIDER TIP</label>
          <textarea
            value={tip}
            onChange={(e) => setTip(e.target.value)}
            data-testid="sunday-clip-tip"
            rows={2}
            className="w-full border-2 border-[#2d2a32] rounded-md bg-white p-3 text-sm mt-1 focus:outline-none focus:border-[#f2a93b]"
            placeholder="One thing you learned this week (e.g. 'Kroger digital coupons now stack with paper')."
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] text-[#5c5c5c] font-semibold">MYSTERY DEAL TEASER · reply-to-unlock</label>
          <textarea
            value={mystery}
            onChange={(e) => setMystery(e.target.value)}
            data-testid="sunday-clip-mystery"
            rows={2}
            className="w-full border-2 border-[#2d2a32] rounded-md bg-white p-3 text-sm mt-1 focus:outline-none focus:border-[#f7c873]"
            placeholder="Dangle a killer deal that requires an email reply. Boosts inbox reputation."
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={doAutoDraft}
          disabled={busy}
          data-testid="sunday-clip-ai-draft-btn"
          className="h-10 px-4 border-2 border-[#2d2a32] rounded-full bg-gradient-to-r from-[#ff6b6b] to-[#f7c873] text-[#2d2a32] hover:brightness-110 text-[11px] tracking-[0.2em] font-bold disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          ✨ AUTO-DRAFT WITH CLAUDE
        </button>
        <button
          onClick={doPreview}
          disabled={busy}
          data-testid="sunday-clip-preview-btn"
          className="h-10 px-4 border-2 border-[#2d2a32] rounded-full bg-white hover:bg-[#f7c873] text-[11px] tracking-[0.2em] font-bold disabled:opacity-50"
        >
          PREVIEW
        </button>
        <button
          onClick={doSendTest}
          disabled={busy}
          data-testid="sunday-clip-test-btn"
          className="h-10 px-4 border-2 border-[#2d2a32] rounded-full bg-white hover:bg-[#4ecdc4] text-[11px] tracking-[0.2em] font-bold disabled:opacity-50"
        >
          SEND TEST TO ME
        </button>
        <button
          onClick={doBroadcast}
          disabled={busy}
          data-testid="sunday-clip-broadcast-btn"
          className="h-10 px-4 border-2 border-[#2d2a32] rounded-full bg-[#2d2a32] text-white hover:bg-[#ff6b6b] text-[11px] tracking-[0.2em] font-bold disabled:opacity-50"
        >
          BROADCAST TO ALL
        </button>
      </div>

      {preview && (
        <div className="mt-4 border-2 border-dashed border-[#2d2a32] rounded-md bg-white overflow-hidden" data-testid="sunday-clip-preview">
          <div className="px-4 py-2 text-[10px] tracking-[0.25em] text-[#5c5c5c] font-bold border-b border-dashed border-[#2d2a32]/40 flex items-center justify-between">
            <span>EMAIL PREVIEW</span>
            <span>{preview.hero_titles?.join(" · ") || "—"}</span>
          </div>
          <iframe
            title="Sunday Clip preview"
            srcDoc={preview.html}
            className="w-full"
            style={{ height: 720, border: 0, background: "#c9b89a" }}
          />
        </div>
      )}
    </section>
  );
}
